import * as React from 'react';

import { normalizeStyle, StyleSheet, type TextProps, type TextStyle, type ViewProps } from '../web';

export type DOMViewTag = keyof HTMLElementTagNameMap;

/**
 * View with a native DIV that can be accessed by a ref.
 */
export function DOMView<Tag extends DOMViewTag = 'div'>({
    ref,
    ...props
}: (Omit<React.DOMAttributes<HTMLElementTagNameMap[Tag]>, keyof ViewProps> &
    ViewProps & {
        children?: React.ReactNode;
        editable?: boolean;
        spellCheck?: boolean;
        tag?: Tag;
        translate?: boolean;
    }) & { ref?: React.Ref<HTMLElement> }) {
    const { editable, tag = 'div', style, translate, ...rest } = props;

    return React.createElement(tag, {
        ...rest,
        ref,
        contentEditable: editable,
        style: normalizeStyle([isInlineTag(tag) ? null : styles.domDivView, style]),
        ...(typeof translate === 'boolean' ? { translate: translate ? 'yes' : 'no' } : {}),
    });
}

/**
 * Text with a native span where DOM attributes can be passed.
 */
export function DOMText<Tag extends DOMViewTag = 'span'>({
    ref,
    ...props
}: (Omit<React.DOMAttributes<HTMLElementTagNameMap[Tag]>, keyof TextProps> &
    TextProps & {
        children?: React.ReactNode;
        editable?: boolean;
        selectable?: boolean;
        spellCheck?: boolean;
        tag?: Tag;
    }) & { ref?: React.Ref<HTMLElement> }) {
    const { editable, selectable, tag = 'span', style, ...rest } = props;

    return React.createElement(tag, {
        ...rest,
        ref,
        contentEditable: editable,
        style: normalizeStyle([selectable && styles.domTextSelectable, styles.domText, style]),
    });
}

function isInlineTag(tag: DOMViewTag): boolean {
    return ['span', 'strong', 'code', 'em', 'mark'].includes(tag);
}

/**
 * Render a view that is not content editable.
 */
export function NonEditableView(
    props: ViewProps & {
        children: React.ReactNode;
        tag?: DOMViewTag;
    }
): React.ReactElement {
    return React.createElement(DOMView, { ...props, editable: false } as any);
}

const styles = StyleSheet.create({
    domDivView: {
        // Same reset style used by the web view primitive.
        alignItems: 'stretch',
        borderStyle: 'solid',
        borderColor: 'black',
        borderWidth: 0,
        boxSizing: 'border-box',
        display: 'flex',
        flexBasis: 'auto',
        flexDirection: 'column',
        flexShrink: 0,
        margin: 0,
        minHeight: 0,
        minWidth: 0,
        padding: 0,
        position: 'relative',
        zIndex: 0,
    },
    domText: {
        // reset margin for text so we can use DomText with headings
        margin: 0,
    },
    domTextSelectable: {
        userSelect: 'text',
    } as TextStyle,
});
